// Sfondi virtuali ALA Meet — 6.1(a) raffinato.
//
// PIPELINE:
//   <video raw>
//     ↓ ImageSegmenter (MediaPipe Tasks Vision, GPU delegate WebGL)
//     ↓ ConfidenceMask (Float32, model-resolution)
//     ↓ smoothing temporale EMA (frame-to-frame, alpha per profilo)
//     ↓ resize a viewport-resolution + edge feather (slope alpha)
//     ↓ canvas composito: persona = video × maskAlpha, sfondo = preset × (1-maskAlpha)
//   <canvas>.captureStream(30) → producer track
//
// PROFILI QUALITÀ ("alto" desktop / "eco" mobile / "auto"):
//
//   ┌─────────────┬────────────┬─────────┬──────────┬───────────┬───────┐
//   │ Profilo     │ Modello    │ Res inf │ Smoothing│ Feather   │ FPS   │
//   ├─────────────┼────────────┼─────────┼──────────┼───────────┼───────┤
//   │ eco         │ landscape  │ 144×256 │ off      │ 2px       │ 30    │
//   │ alto        │ multiclass │ 256×256 │ EMA 0.6  │ 4px       │ 30    │
//   └─────────────┴────────────┴─────────┴──────────┴───────────┴───────┘
//
// COSTO CPU dichiarato (per frame, GPU-accelerato dove disponibile):
//   - Inferenza light landscape 144×256 GPU : ~3–5 ms (laptop) / 8–14 ms (mobile mid)
//   - Inferenza multiclass 256×256 GPU      : ~8–14 ms (laptop) / 20–35 ms (mobile mid)
//   - EMA smoothing su Float32 maskData     : ~1 ms / 2 ms
//   - Edge feather slope (linear ramp)      : ~2 ms (2px) / 4 ms (4px)
//   - Canvas composite (drawImage destination-in) : ~3–5 ms
//
//   Budget totale a 30 fps = 33 ms.
//   • eco mobile (~16 ms) → ampio margine.
//   • alto desktop (~22 ms) → margine adeguato.
//   • alto mobile (~45 ms) → sconsigliato: viene FORZATO 'eco' su touch device.
//
// FALLBACK:
//   - !WebGL → toggle nascosto interamente
//   - init() throw → torna a 'none' + segnala errore
//   - inferenza > 50ms p95 per 30 frame consecutivi → auto-downgrade a 'eco'

import { ImageSegmenter, FilesetResolver, ImageSegmenterResult } from '@mediapipe/tasks-vision';
import { paintStyle, STYLE_META, type StyleId } from './backgrounds-stylized.js';

export type EffectId = string;
export type Profile = 'auto' | 'eco' | 'alto';

export interface EffectMeta {
  id: EffectId;
  label: string;
  description: string;
  /** 'none' = passa raw; 'paint' = dipinto via canvas2d; 'image' = bitmap lazy-load */
  kind: 'none' | 'paint' | 'image';
  /** Per 'image': URL relativo o assoluto. Per 'image-custom': in IndexedDB. */
  imageUrl?: string;
  /** Per credit/licenza. Visibile in tooltip. */
  attribution?: string;
}

export interface ProfileConfig {
  modelUrl: string;
  modelWidth: number;
  modelHeight: number;
  smoothAlpha: number; // 0 = no smoothing, [0..1] EMA factor for current frame
  featherPx: number;
}

const MODEL_LIGHT = 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/1/selfie_segmenter.tflite';
const MODEL_MULTICLASS = 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/1/selfie_multiclass_256x256.tflite';
const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.20/wasm';

const PROFILE_ECO: ProfileConfig = {
  modelUrl: MODEL_LIGHT, modelWidth: 256, modelHeight: 144,
  smoothAlpha: 0, featherPx: 2,
};
const PROFILE_HIGH: ProfileConfig = {
  modelUrl: MODEL_MULTICLASS, modelWidth: 256, modelHeight: 256,
  smoothAlpha: 0.6, featherPx: 4,
};

export function detectProfile(): 'eco' | 'alto' {
  if (typeof matchMedia === 'function' && matchMedia('(hover: none) and (pointer: coarse)').matches) return 'eco';
  const cores = navigator.hardwareConcurrency ?? 4;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  if (cores < 4 || mem < 4) return 'eco';
  return 'alto';
}

export function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('webgl2') || canvas.getContext('webgl');
    return Boolean(ctx);
  } catch { return false; }
}

// ── Catalogo built-in (paint procedurali + slot per immagini ratificate dopo). ──

export const EFFECTS: EffectMeta[] = [
  { id: 'none', label: 'Nessuno', kind: 'none', description: 'Video originale, zero impatto CPU.' },
  { id: 'blur', label: 'Sfocato', kind: 'paint', description: 'Sfondo sfocato, persona nitida.' },
  // 7 sfondi stilizzati ratificati 2026-06-12 (palette ALA navy/oro/sabbia).
  ...STYLE_META.map((m): EffectMeta => ({ id: m.id, label: m.label, kind: 'paint', description: m.description })),
];

/** Registra un nuovo effetto (es. dopo ratifica delle foto reali o upload custom). */
export function registerEffect(meta: EffectMeta): void {
  if (EFFECTS.some((e) => e.id === meta.id)) return;
  EFFECTS.push(meta);
}

// ── IndexedDB per sfondi personalizzati ──

const IDB_NAME = 'ala-meet-bg';
const IDB_STORE = 'backgrounds';

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

interface IdbBackground { id: string; name: string; blob: Blob; addedAt: number }

export async function idbList(): Promise<IdbBackground[]> {
  try {
    const db = await idbOpen();
    return await new Promise<IdbBackground[]>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).getAll();
      req.onsuccess = () => resolve(req.result as IdbBackground[]);
      req.onerror = () => reject(req.error);
    });
  } catch { return []; }
}

export async function idbAdd(name: string, blob: Blob): Promise<IdbBackground> {
  const db = await idbOpen();
  const id = 'custom-' + Math.random().toString(36).slice(2, 10);
  const record: IdbBackground = { id, name, blob, addedAt: Date.now() };
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  return record;
}

export async function idbDelete(id: string): Promise<void> {
  try {
    const db = await idbOpen();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch { /* */ }
}

// ── Classe principale ──

export class BackgroundEffects {
  private profile: Profile = 'auto';
  private resolvedProfile: 'eco' | 'alto' = 'eco';
  private cfg: ProfileConfig = PROFILE_ECO;
  private segmenter: ImageSegmenter | null = null;
  private fileset: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>> | null = null;
  private currentEffectId: EffectId = 'none';
  private inputVideo: HTMLVideoElement;
  private inputStream: MediaStream | null = null;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private outputStream: MediaStream | null = null;
  private bgPaintCache = new Map<EffectId, HTMLCanvasElement>();
  private imageCache = new Map<EffectId, HTMLImageElement>();
  private smoothMask: Float32Array | null = null;
  private smoothMaskW = 0;
  private smoothMaskH = 0;
  private active = false;
  private disposed = false;
  /** Stats per auto-downgrade. */
  private inferenceCostMs: number[] = [];
  private onPerformanceDrop: ((newProfile: 'eco') => void) | null = null;

  constructor() {
    this.inputVideo = document.createElement('video');
    this.inputVideo.autoplay = true;
    this.inputVideo.playsInline = true;
    this.inputVideo.muted = true;
    this.canvas = document.createElement('canvas');
    this.canvas.width = 640;
    this.canvas.height = 480;
    const ctx = this.canvas.getContext('2d', { willReadFrequently: false });
    if (!ctx) throw new Error('canvas 2d ctx unavailable');
    this.ctx = ctx;
  }

  setOnPerformanceDrop(fn: (newProfile: 'eco') => void): void { this.onPerformanceDrop = fn; }

  async setProfile(p: Profile): Promise<void> {
    this.profile = p;
    const resolved = p === 'auto' ? detectProfile() : p;
    if (resolved === this.resolvedProfile && this.segmenter) return;
    this.resolvedProfile = resolved;
    this.cfg = resolved === 'alto' ? PROFILE_HIGH : PROFILE_ECO;
    if (this.segmenter) {
      try { this.segmenter.close(); } catch { /* */ }
      this.segmenter = null;
    }
    this.inferenceCostMs = [];
    if (this.currentEffectId !== 'none') await this.ensureSegmenter();
  }

  getResolvedProfile(): 'eco' | 'alto' { return this.resolvedProfile; }
  getProfile(): Profile { return this.profile; }

  private async ensureSegmenter(): Promise<void> {
    if (this.segmenter || this.disposed) return;
    if (!isWebGLAvailable()) throw new Error('WebGL non disponibile');
    if (!this.fileset) this.fileset = await FilesetResolver.forVisionTasks(WASM_BASE);
    this.segmenter = await ImageSegmenter.createFromOptions(this.fileset, {
      baseOptions: { modelAssetPath: this.cfg.modelUrl, delegate: 'GPU' },
      runningMode: 'VIDEO',
      outputCategoryMask: false,
      outputConfidenceMasks: true,
    });
  }

  async init(profile?: Profile): Promise<void> {
    if (profile) await this.setProfile(profile);
    else if (this.profile === 'auto') await this.setProfile('auto');
    await this.ensureSegmenter();
  }

  async setInputStream(stream: MediaStream | null): Promise<void> {
    // Forza il REWIRING anche se lo stream object è lo stesso ma la track è cambiata
    // o è 'ended' (camera off/on, OS sleep): srcObject = null prima di riassegnare
    // perché Safari/Chrome non triggrano un nuovo loadedmetadata su same-object.
    this.inputStream = stream;
    this.inputVideo.srcObject = null;
    // Reset EMA: la maschera precedente non è più valida (track diversa, dimensione
    // potenzialmente diversa, e altrimenti EMA "ricorda" lo sfondo precedente).
    this.smoothMask = null;
    this.smoothMaskW = 0;
    this.smoothMaskH = 0;
    if (stream) {
      this.inputVideo.srcObject = stream;
      // play() risolve in genere DOPO loadedmetadata su Chrome/Firefox; su Safari
      // a volte risolve prima. Attendiamo esplicitamente videoWidth>0 per robustezza
      // (timeout 3s che non blocca: passiamo oltre col canvas a dimensione settings).
      try { await this.inputVideo.play(); } catch { /* */ }
      if (this.inputVideo.videoWidth === 0) {
        await new Promise<void>((resolve) => {
          const onMeta = () => { this.inputVideo.removeEventListener('loadedmetadata', onMeta); resolve(); };
          this.inputVideo.addEventListener('loadedmetadata', onMeta, { once: true });
          setTimeout(() => { this.inputVideo.removeEventListener('loadedmetadata', onMeta); resolve(); }, 3000);
        });
      }
      const t = stream.getVideoTracks()[0];
      const s = t?.getSettings();
      const w = this.inputVideo.videoWidth || s?.width || 640;
      const h = this.inputVideo.videoHeight || s?.height || 480;
      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
        this.bgPaintCache.clear();
      }
    } else {
      this.stopLoop();
    }
  }

  /**
   * Garantisce che il canvas sia disegnato almeno una volta prima che
   * getOutputStream venga consumato. Risolve il freeze "applico sfondo →
   * video congelato" causato da captureStream attivato prima del primo frame:
   * mediasoup-client.replaceTrack inietta una track che non ha mai emesso e
   * resta vista come "ultimo frame" lato peer remoti.
   */
  async primeOutput(): Promise<void> {
    if (this.disposed) return;
    // Se l'input non e' pronto, fallback a un fill nero: meglio un frame nero
    // di una track silenziosa che congela l'ultima immagine vista.
    if (!this.inputVideo.videoWidth || !this.segmenter) {
      this.ctx.fillStyle = '#000';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    } else {
      try { this.renderFrame(); }
      catch (e) { console.warn('[effects] primeOutput frame', e); }
    }
    // Attendi un rAF: captureStream campiona il canvas al ritmo dei paint del
    // browser; un rAF garantisce che il frame appena dipinto sia pubblicato.
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }

  async setEffect(id: EffectId): Promise<void> {
    if (this.disposed) return;
    this.currentEffectId = id;
    if (id === 'none') {
      this.stopLoop();
      return;
    }
    // Se è 'image' o 'image-custom' pre-carica la bitmap
    const meta = EFFECTS.find((e) => e.id === id);
    if (meta?.kind === 'image' && meta.imageUrl) await this.loadImage(id, meta.imageUrl);
    if (meta?.kind === 'image' && !meta.imageUrl && id.startsWith('custom-')) {
      // sfondo personalizzato in IndexedDB
      const all = await idbList();
      const found = all.find((x) => x.id === id);
      if (found) {
        const url = URL.createObjectURL(found.blob);
        await this.loadImage(id, url);
      }
    }
    await this.ensureSegmenter();
    this.startLoop();
  }

  getEffect(): EffectId { return this.currentEffectId; }

  /** MediaStream dal canvas composito a 30fps. Da passare a producer.replaceTrack.
   *  Se l'output corrente ha una track 'ended' (capita dopo lunghe pause o resize
   *  hardware del canvas), la ricreiamo per non passare al producer una track morta. */
  getOutputStream(): MediaStream {
    const stale = this.outputStream && this.outputStream.getVideoTracks().some((t) => t.readyState !== 'live');
    if (!this.outputStream || stale) {
      if (this.outputStream) {
        try { this.outputStream.getTracks().forEach((t) => t.stop()); } catch { /* */ }
      }
      this.outputStream = this.canvas.captureStream(30);
    }
    return this.outputStream;
  }

  /**
   * Watchdog di contenuto per la pipeline sfondo: ritorna true se il canvas
   * sta producendo frame interamente neri (bug 0.9.5-server / 13-06: il
   * compositing produce output lum=0 anche se il track è live e i frame
   * "scorrono" — il toggle camera manuale lo aggira ri-acquisendo la sorgente).
   * Sampling cheap: una sola striscia di 4 righe al centro del canvas.
   */
  isCanvasBlack(): boolean {
    try {
      const w = this.canvas.width;
      const h = this.canvas.height;
      if (w === 0 || h === 0) return false;
      const stripY = Math.floor(h / 2);
      const stripH = Math.min(4, h - stripY);
      const data = this.ctx.getImageData(0, stripY, w, stripH).data;
      let total = 0;
      let pixels = 0;
      for (let i = 0; i < data.length; i += 4) {
        total += data[i]! + data[i + 1]! + data[i + 2]!;
        pixels++;
      }
      const avg = pixels > 0 ? total / (pixels * 3) : 0;
      return avg < 5; // soglia lum < 5/255 = nero (i frame validi misurati >40)
    } catch {
      // canvas tainted o ctx non leggibile → non possiamo decidere, return false
      return false;
    }
  }
  getRawStream(): MediaStream | null { return this.inputStream; }

  private async loadImage(id: EffectId, url: string): Promise<void> {
    if (this.imageCache.has(id)) return;
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { this.imageCache.set(id, img); resolve(); };
      img.onerror = () => reject(new Error(`Impossibile caricare lo sfondo: ${url}`));
      img.src = url;
    });
  }

  dispose(): void {
    this.disposed = true;
    this.stopLoop();
    this.inputStream = null;
    this.inputVideo.srcObject = null;
    try { this.outputStream?.getTracks().forEach((t) => t.stop()); } catch { /* */ }
    this.outputStream = null;
    try { this.segmenter?.close(); } catch { /* */ }
    this.segmenter = null;
    this.bgPaintCache.clear();
    this.imageCache.clear();
    this.smoothMask = null;
  }

  // ── Loop ──

  private startLoop(): void {
    if (this.active) return;
    this.active = true;
    const tick = () => {
      if (!this.active || this.disposed) return;
      try { this.renderFrame(); } catch (e) { console.warn('[effects] frame', e); }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
  private stopLoop(): void { this.active = false; }

  // ── Pipeline ──

  private renderFrame(): void {
    if (!this.segmenter || !this.inputVideo.videoWidth) return;
    const v = this.inputVideo;
    if (this.canvas.width !== v.videoWidth || this.canvas.height !== v.videoHeight) {
      this.canvas.width = v.videoWidth;
      this.canvas.height = v.videoHeight;
      this.bgPaintCache.clear();
    }
    const t0 = performance.now();
    this.segmenter.segmentForVideo(v, t0, (result: ImageSegmenterResult) => {
      const cost = performance.now() - t0;
      this.recordInferenceCost(cost);
      const mask = result.confidenceMasks?.[0];
      if (!mask) { result.close(); return; }
      const maskData = mask.getAsFloat32Array();
      // Multiclass: categoria 0 = background. Per "persona" = 1 - bg.
      // Light model: 1 valore di confidenza = persona. Usiamo direttamente.
      const personMask = this.resolvedProfile === 'alto' ? invert(maskData) : maskData;
      // Smoothing EMA temporale
      const smoothed = this.applySmoothing(personMask, mask.width, mask.height);
      this.composite(smoothed, mask.width, mask.height);
      result.close();
    });
  }

  private recordInferenceCost(ms: number): void {
    this.inferenceCostMs.push(ms);
    if (this.inferenceCostMs.length > 30) this.inferenceCostMs.shift();
    // Auto-downgrade se p95 > 50ms su 30 frame e siamo in 'alto'
    if (this.inferenceCostMs.length === 30 && this.resolvedProfile === 'alto') {
      const sorted = [...this.inferenceCostMs].sort((a, b) => a - b);
      const p95 = sorted[Math.floor(sorted.length * 0.95)]!;
      if (p95 > 50) {
        this.inferenceCostMs = [];
        void this.setProfile('eco');
        if (this.onPerformanceDrop) this.onPerformanceDrop('eco');
      }
    }
  }

  private applySmoothing(mask: Float32Array, w: number, h: number): Float32Array {
    if (this.cfg.smoothAlpha <= 0) return mask;
    if (!this.smoothMask || this.smoothMaskW !== w || this.smoothMaskH !== h) {
      this.smoothMask = new Float32Array(mask);
      this.smoothMaskW = w;
      this.smoothMaskH = h;
      return this.smoothMask;
    }
    const a = this.cfg.smoothAlpha;
    const inv = 1 - a;
    const buf = this.smoothMask;
    for (let i = 0; i < buf.length; i++) {
      buf[i] = a * mask[i]! + inv * buf[i]!;
    }
    return buf;
  }

  private composite(personMask: Float32Array, mW: number, mH: number): void {
    const w = this.canvas.width;
    const h = this.canvas.height;
    const ctx = this.ctx;

    // 1) Sfondo
    const bg = this.getBackgroundCanvas(this.currentEffectId);
    if (bg) {
      ctx.drawImage(bg, 0, 0, w, h);
    } else if (this.currentEffectId === 'blur') {
      ctx.save();
      ctx.filter = 'blur(18px)';
      ctx.drawImage(this.inputVideo, 0, 0, w, h);
      ctx.filter = 'none';
      ctx.restore();
    }

    // 2) Persona: video corrente mascherato → drawImage destination-in
    const tmp = getTempCanvas(w, h);
    const tctx = tmp.getContext('2d')!;
    tctx.clearRect(0, 0, w, h);
    tctx.drawImage(this.inputVideo, 0, 0, w, h);
    const maskCv = paintMask(personMask, mW, mH, this.cfg.featherPx);
    tctx.globalCompositeOperation = 'destination-in';
    tctx.drawImage(maskCv, 0, 0, w, h);
    tctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(tmp, 0, 0);
  }

  private getBackgroundCanvas(id: EffectId): HTMLCanvasElement | null {
    if (id === 'blur' || id === 'none') return null;
    const meta = EFFECTS.find((e) => e.id === id);
    if (meta?.kind === 'image') {
      const img = this.imageCache.get(id);
      if (!img) return null;
      const cv = this.bgPaintCache.get(id);
      const w = this.canvas.width;
      const h = this.canvas.height;
      if (cv && cv.width === w && cv.height === h) return cv;
      const out = document.createElement('canvas');
      out.width = w; out.height = h;
      const octx = out.getContext('2d')!;
      // cover (centered)
      const ar = img.width / img.height;
      const tar = w / h;
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (ar > tar) { sw = img.height * tar; sx = (img.width - sw) / 2; }
      else          { sh = img.width / tar; sy = (img.height - sh) / 2; }
      octx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
      this.bgPaintCache.set(id, out);
      return out;
    }
    if (this.bgPaintCache.has(id)) return this.bgPaintCache.get(id)!;
    const c = paintStylizedBackground(id, this.canvas.width, this.canvas.height);
    if (c) this.bgPaintCache.set(id, c);
    return c;
  }
}

/** Paint stylized backgrounds: delega al modulo backgrounds-stylized.ts. */
function paintStylizedBackground(id: EffectId, w: number, h: number): HTMLCanvasElement | null {
  if (!STYLE_META.some((m) => m.id === id)) return null;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d');
  if (!ctx) return null;
  paintStyle(id as StyleId, ctx, w, h);
  return cv;
}

/** Genera una mini-thumbnail dataURL per il modale Effetti. Cache per id. */
const THUMB_CACHE = new Map<EffectId, string>();
export function getEffectThumbnail(id: EffectId, width = 200, height = 120): string | null {
  if (id === 'none' || id === 'blur') return null; // gestiti CSS
  if (THUMB_CACHE.has(id)) return THUMB_CACHE.get(id)!;
  if (!STYLE_META.some((m) => m.id === id)) return null;
  const cv = document.createElement('canvas');
  cv.width = width; cv.height = height;
  const ctx = cv.getContext('2d');
  if (!ctx) return null;
  paintStyle(id as StyleId, ctx, width, height);
  const url = cv.toDataURL('image/jpeg', 0.85);
  THUMB_CACHE.set(id, url);
  return url;
}

// ── Helpers ──

function invert(src: Float32Array): Float32Array {
  const out = new Float32Array(src.length);
  for (let i = 0; i < src.length; i++) out[i] = 1 - src[i]!;
  return out;
}

let TEMP: HTMLCanvasElement | null = null;
function getTempCanvas(w: number, h: number): HTMLCanvasElement {
  if (!TEMP) TEMP = document.createElement('canvas');
  if (TEMP.width !== w || TEMP.height !== h) { TEMP.width = w; TEMP.height = h; }
  return TEMP;
}

let MASK: HTMLCanvasElement | null = null;
/** Edge feather adattivo: applica una rampa lineare invece di un hard-threshold. */
function paintMask(personMask: Float32Array, mW: number, mH: number, featherPx: number): HTMLCanvasElement {
  if (!MASK) MASK = document.createElement('canvas');
  if (MASK.width !== mW || MASK.height !== mH) { MASK.width = mW; MASK.height = mH; }
  const mctx = MASK.getContext('2d')!;
  const img = mctx.createImageData(mW, mH);
  // feather slope: 1 - 2*featherPx/mW range attorno a 0.5
  // Più featherPx = ramp più ampia = bordi più morbidi
  const slope = Math.max(0.06, Math.min(0.5, featherPx / 12));
  const lo = 0.5 - slope;
  const hi = 0.5 + slope;
  const range = hi - lo;
  for (let i = 0; i < personMask.length; i++) {
    const v = personMask[i]!;
    let a: number;
    if (v <= lo) a = 0;
    else if (v >= hi) a = 255;
    else a = Math.round(((v - lo) / range) * 255);
    const off = i * 4;
    img.data[off] = 255; img.data[off + 1] = 255; img.data[off + 2] = 255;
    img.data[off + 3] = a;
  }
  mctx.putImageData(img, 0, 0);
  return MASK;
}

// (paintProcedural rimosso: la pittura è ora delegata a backgrounds-stylized.ts via paintStylizedBackground.)
